import {
        CandleLightingEvent, HavdalahEvent, HDate, HebrewCalendar, Location, Molad, MoladEvent, OmerEvent, ParshaEvent, RoshChodeshEvent, RoshHashanaEvent, Zmanim, flags
    } from "@hebcal/core"
import '@hebcal/locales'
import {displayChabadZmanim, chabadDailyZmanim, getNextRestPeriod as calculateNextRestPeriod} from '@kehila/zmanim'
import tzlookup from "tz-lookup"

// HEBCAL FUNCTIONS

export default class Hebcal {
    DAYArray = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"]

    HEBCAL = []

    ORG = null

    LAT =null

    LON=null

    NOW=new Date()

    constructor(org=null, now = new Date(), lat="48.8534", lon="2.3488") {
        this.ORG = org
        this.NOW=now
        if(org){
            this.LAT=org.lat
            this.LON=org.long
        }else{
            this.LAT=lat
            this.LON=lon
        }

    }

    /**
     * Fuseau horaire IANA (ex "Europe/Bucharest") déduit des coordonnées de
     * l'org via tz-lookup (offline, aucun appel réseau). Mémoïsé.
     *
     * INDISPENSABLE pour afficher les horaires astronomiques (entrée/sortie de
     * Chabbat, zmanim). @hebcal/core renvoie des Date = instants UTC réels ;
     * sans ce fuseau, toute conversion en heure murale (toLocaleTimeString ou
     * getHours) utilise le fuseau du serveur/navigateur → décalage pour toute
     * org hors de ce fuseau (ex : Roumanie UTC+3 affichée depuis la France
     * UTC+2 = 1h de moins).
     */
    getTimezone() {
        if (this._tz !== undefined) return this._tz
        this._tz = null
        try {
            const lat = parseFloat(this.LAT)
            const lon = parseFloat(this.LON)
            if (!isNaN(lat) && !isNaN(lon)) {
                this._tz = tzlookup(lat, lon)
            }
        } catch (e) {
            this._tz = null
        }
        return this._tz
    }

    /**
     * Formate une Date astronomique (instant UTC) en heure murale "HH:MM" DANS
     * LE FUSEAU DE L'ORG. À utiliser partout où l'on affiche entrée/sortie de
     * Chabbat ou un zman. Renvoie null si la date est invalide.
     */
    formatTime(date, locale = "fr") {
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null
        const tz = this.getTimezone()
        try {
            return new Intl.DateTimeFormat(locale || "fr", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
                timeZone: tz || undefined,
            }).format(date)
        } catch (e) {
            return new Intl.DateTimeFormat(locale || "fr", { hour: "2-digit", minute: "2-digit", hour12: false }).format(date)
        }
    }

    /**
     * Extrait {heure, minute} d'une Date astronomique DANS LE FUSEAU DE L'ORG
     * (et non celui du serveur). À utiliser à la place de getHours()/getMinutes()
     * pour les zmanim variables.
     */
    getWallClock(date) {
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null
        const tz = this.getTimezone()
        try {
            const parts = new Intl.DateTimeFormat("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
                timeZone: tz || undefined,
            }).formatToParts(date)
            const h = parseInt(parts.find(p => p.type === "hour")?.value, 10)
            const m = parseInt(parts.find(p => p.type === "minute")?.value, 10)
            if (isNaN(h) || isNaN(m)) return { heure: date.getHours(), minute: date.getMinutes() }
            return { heure: h, minute: m }
        } catch (e) {
            return { heure: date.getHours(), minute: date.getMinutes() }
        }
    }

    generateHebCal(startDate = this.NOW) {
        const org=this.ORG

        var data = []

        var dateStart = new Date(startDate)
        dateStart.setDate(dateStart.getDate() - 10)

        var dateEnd = new Date(startDate)
        dateEnd.setDate(dateEnd.getDate() + 30)

        const options = {
            start: dateStart,
            end: dateEnd,
            isHebrewYear: false,
            candlelighting: true,
            locale: "fr",
            location: new Location(parseFloat(this.LAT), parseFloat(this.LON)),
            sedrot: true,
            omer: true,
            noModern: true,
            shabbatMevarchim: true,
            molad: true,
            il: org.israel ? true : false,

        }

        const events = HebrewCalendar.calendar(options);

        //console.log("HEBCAL events",events)

        var data2 = {
            hdate: events[0].getDate(),
            date: events[0].getDate().greg(),
            desc: []
        }
        var time = events[0].getDate().greg().getTime()

        for (const ev of events) {
            const hd = ev.getDate();
            const date = hd.greg();

            if (time != date.getTime()) {
                time = date.getTime()

                data.push(data2)
                data2 = {
                    hdate: hd,
                    date: date,
                    desc: []
                }
            }

            data2.desc.push(ev.getDesc())

            if (ev instanceof OmerEvent) {
                data2.omer = ev.omer
            }

            if (ev.getDesc().includes("Rosh Hashana") && !ev.getDesc().includes("Erev")) {
                data2.yomtov = true
                data2.yomtovType = "ROSH_HASHANA"
            }

            if (ev instanceof MoladEvent) {
                //CHABBAT MEVARHIM
                //console.log("MEVARHIM",ev)
                data2.mevarhim = true
                data2.mevarhimMonth = ev.getDesc().replace("Molad", '').split(" ").slice(0, -1).join(" ").trim()
                data2.molad = ev.molad
            }


            if (ev instanceof RoshChodeshEvent) {
                //ROSH HODESH
                //console.log("ROSH HODESH",ev)
                data2.roshHodesh = true
                data2.roshHodeshMonth = ev.getDesc().replace("Rosh Chodesh ", '').trim()
            }

            if (ev instanceof ParshaEvent) {
                data2.parasha = ev.parsha.join("-")
            }

            if (ev instanceof HavdalahEvent) {
                switch (org.zmanimShitat) {

                    case "HABBAD.ORG":
                        data2.sortieShabbat = this.getChabadTimes(date).shabbatEnds
                        break;
                    default:
                        data2.sortieShabbat = new Date(ev.eventTime)
                }
            }

            if (ev instanceof CandleLightingEvent) {
                var dateentree = null

                switch (org.zmanimShitat) {
                    case "HABBAD.ORG":
                        dateentree = this.getChabadTimes(date)[((ev.getFlags() & flags.LIGHT_CANDLES_TZEIS) || date.getDay() === 6) ? 'shabbatEnds' : 'candleLighting']
                        break;
                    default:
                        dateentree = new Date(ev.eventTime)
                }

                data2.entreeShabbat = dateentree
            }

            //console.log(date.toLocaleDateString(), ev.render(), hd.toString(),ev.getDesc());

        }

        this.HEBCAL = data

        //console.log("HEBCAL",HEBCAL)
    }

    getNextRestPeriod(date = this.NOW) {
        const customMinutes = this.ORG?.chabadCandleLightingMinutes
        return calculateNextRestPeriod({
            from: new Date(date),
            latitude: this.LAT,
            longitude: this.LON,
            israel: !!this.ORG?.israel,
            useChabad: this.ORG?.zmanimShitat === "HABBAD.ORG",
            candleLightingMinutes: customMinutes !== undefined && customMinutes !== null && customMinutes !== ''
                ? Number(customMinutes) : undefined,
        })
    }

    getNextParasha(date) {
        return this.getNextRestPeriod(date)?.occasion.parasha?.latin || null
    }

    getNextEntreeShabbat(date) {
        return this.getNextRestPeriod(date)?.start || null
    }

    getNextSortieShabbat(date) {
        return this.getNextRestPeriod(date)?.end || "error"
    }


    getDateNowFromZman(date=this.NOW) {
        const zmanim = new Zmanim(new Date(date), parseFloat(this.LAT), parseFloat(this.LON))

        if (Date.parse(date) > zmanim.tzeit(7.083).getTime()) {
            var newdate = new Date(date)
            newdate.setDate(newdate.getDate() + 1);
            return newdate
        }

        return date
    }

    getHebrewDate() {
        var date = this.getDateNowFromZman(new Date())

        var hdate = new HDate(date)

        return this.DAYArray[date.getDay()] + " " + hdate.getDate() + " " + hdate.getMonthName() + " " + hdate.getFullYear()
    }

    hdate(now = new Date()) {
        return new HDate(now)
    }

    getHebreuYear(now = new Date()) {
        return new HDate(now).getFullYear()
    }

    getHazkara(nichmat, anneeHE) {

        const nowHE = new HDate(this.getDateNowFromZman())

        var hazkara = new HDate(nichmat.jourHE, nichmat.moisHE, anneeHE)


        //CHECHVAN 30/29
        if (nichmat.jourHE == 30 && nichmat.moisHE == "Cheshvan" && HDate.longCheshvan(anneeHE) == false) {
            hazkara = new HDate(29, nichmat.moisHE, anneeHE)
        }

        //KISLEV 30/29
        if (nichmat.jourHE == 30 && nichmat.moisHE == "Kislev" && HDate.shortKislev(anneeHE) == true) {
            hazkara = new HDate(29, nichmat.moisHE, anneeHE)
        }


        if (hazkara.getFullYear() == nowHE.getFullYear()) {
            if (
                (hazkara.getTishreiMonth() == nowHE.getTishreiMonth() && hazkara.getDate() < nowHE.getDate())
                || hazkara.getTishreiMonth() < nowHE.getTishreiMonth()
            ) {
                return this.getHazkara(nichmat, anneeHE + 1)
            }
        }


        return {
            jourHaz: hazkara.getDate(),
            moisHaz: hazkara.getMonthName(),
            anneeHaz: hazkara.getFullYear(),
            greg:hazkara.greg()
        }
    }


    getDiffHDate(item) {
        const nowHE = new HDate(this.getDateNowFromZman())
        const hazkaraHE = new HDate(item.jourHaz, item.moisHaz, item.anneeHaz)

        return hazkaraHE.deltaDays(nowHE)
    }

    getGregDate(item) {
        const hdate = new HDate(item.jourHaz, item.moisHaz, item.anneeHaz)
        return hdate.greg()
    }


    getDateGematria(item) {
        var dateHE = new HDate(item.jourHE, item.moisHE, item.anneeHE)

        return dateHE.renderGematriya()
    }

    getHebCalByDate(date) {
        date = ('0' + date.getDate()).slice(-2) + "/" + ('0' + (date.getMonth() + 1)).slice(-2) + "/" + date.getFullYear()

        for (var e of this.HEBCAL) {
            const date2 = ('0' + e.date.getDate()).slice(-2) + "/" + ('0' + (e.date.getMonth() + 1)).slice(-2) + "/" + e.date.getFullYear()
            if (date2 == date) {

                return e
            }
        }

        return false
    }

    getVariableZman(type, date) {
        const config=this.ORG

        date = new Date(date)
        date.setHours(0, 0, 0, 0);

        switch (config.zmanimShitat) {
            case "HABBAD.ORG":
                return this.getVariableZmanHabbad(type, date)
            default:
                return this.getVariableZmanDefault(type, date)
        }

    }

//CALLJ
    getVariableZmanDefault(type, date) {

        const zmanim = new Zmanim(new Date(date), parseFloat(this.LAT), parseFloat(this.LON))
        switch (type) {
            case "NETS":
                return zmanim.neitzHaChama()
            case "HATSOT":
                return zmanim.chatzot()
            case "PLAG":
                return zmanim.plagHaMincha()
            case "CHKIAH":
                return zmanim.shkiah()
            case "TSET":
                return zmanim.tzeit(7.083)
            case "ENTREE_CHABBAT":
                return this.getHebCalByDate(date).entreeShabbat
            case "SORTIE_CHABBAT":
                return this.getHebCalByDate(date).sortieShabbat
        }

        return null
    }

    getChabadTimes(date) {
        const customMinutes = this.ORG?.chabadCandleLightingMinutes
        const candleLightingMinutes = customMinutes !== undefined && customMinutes !== null && customMinutes !== ''
            ? Number(customMinutes)
            : (this.ORG?.israel ? 22 : 18)
        return displayChabadZmanim({
            date,
            latitude: this.LAT,
            longitude: this.LON,
            candleLightingMinutes,
        })
    }

//HABAD.ORG
    getVariableZmanHabbad(type, date) {
        const times = this.getChabadTimes(date)

        switch (type) {
            case "NETS":
                return times.sunrise
            case "HATSOT":
                return times.midday
            case "PLAG":
                return times.plag
            case "CHKIAH":
                return times.sunset
            case "TSET":
                return times.tzeit
            case "ENTREE_CHABBAT":
                return this.getHebCalByDate(date).entreeShabbat
            case "SORTIE_CHABBAT":
                return this.getHebCalByDate(date).sortieShabbat
        }

        return null
    }

    getZmanimDay(date = new Date()) {
        var nextFound = false

        var data = null

        switch (this.ORG.zmanimShitat) {

            case "HABBAD.ORG":
                data = this.getZmanimHabad(date)
                break;
            default:
                data = this.getZmanimDefault(date)
                break;
        }


        data.sort((a, b) => a.time - b.time)

        var dataFinal = []

        for (var item of data) {
            if (!nextFound && item.time > new Date()) {
                nextFound = true
                item.next = true
            }
            item.time = Zmanim.roundTime(item.time)
            dataFinal.push(item)
        }

        return data
    }


    isOmer() {
        var hebcal = this.getHebCalByDate(new Date())

        if (hebcal.omer) {
            return true
        }
        return false
    }


    needMolad() {
        var hebcal = this.getHebCalByDate(new Date())

        if (hebcal.mevarhim) {
            return true
        }
        return false
    }


//HABBD.ORG
    getZmanimHabad(date = new Date()) {
        return chabadDailyZmanim({
            date,
            latitude: this.LAT,
            longitude: this.LON,
        }, {includeDeoraita: !this.ORG.israel})
    }

//CALJ
    getZmanimDefault(date = new Date()) {
        var data = []

        const zmanim = new Zmanim(new Date(date),parseFloat(this.LAT), parseFloat(this.LON))

        data.push({
            nom: "עלות השחר",
            time: zmanim.alotHaShachar()
        })

        data.push({
            nom: "תפילין ושמע",
            time: zmanim.misheyakir()
        })

        data.push({
            nom: "נץ החמה",
            time: zmanim.neitzHaChama()
        })

        data.push({
            nom: "סוף זמן ק״ש מ״א",
            time: zmanim.sofZmanShmaMGA()
        })

        data.push({
            nom: "סוף זמן ק״ש גר״א",
            time: zmanim.sofZmanShma()
        })

        data.push({
            nom: "סוף זמן תפילה מ״א",
            time: zmanim.sofZmanTfillaMGA()
        })

        data.push({
            nom: "סוף זמן תפילה גר״א",
            time: zmanim.sofZmanTfilla()
        })

        data.push({
            nom: "חצות היום",
            time: zmanim.chatzot()
        })

        data.push({
            nom: "מנחה גדולה",
            time: zmanim.minchaGedola()
        })

        data.push({
            nom: "מנחה קטנה",
            time: zmanim.minchaKetana()
        })

        data.push({
            nom: "פלג המנחה",
            time: zmanim.plagHaMincha()
        })

        data.push({
            nom: "שקיעת החמה",
            time: zmanim.shkiah()
        })

        data.push({
            nom: "צאת הכוכבים",
            time: zmanim.tzeit(7.083)
        })

        data.push({
            nom: "צאת הכוכבים ר'ת",
            time: zmanim.tzeit(8.5)
        })

        data.push({
            nom: "חצות לילה",
            time: zmanim.chatzotNight()
        })


        return data
    }


    getNextRoshHosdesh(date) {
        var data = []
        var hd = new HDate(date);
        const month = hd.getMonthName()
        var nextMonth = null

        var i = 0
        while (nextMonth == null) {
            hd = hd.add(i)
            //console.log(hd)
            if (month != hd.getMonthName()) {
                nextMonth = hd.getMonthName()
            }
            i++
        }

        for (var h of this.HEBCAL) {
            if (h.roshHodesh && h.roshHodeshMonth == nextMonth) {
                data.push(h)
            }
        }

        return data
    }


    getMolad(month) {

        for (var h of this.HEBCAL) {
            if (h.mevarhim && h.mevarhimMonth == month) {
                return h.molad
            }
        }

        return null
    }


    isChabbat() {
        var date = this.getDateNowFromZman(new Date())

        if (date.getDay() == 6) {
            return true
        }

        return false
    }


    isYomTov() {
        var hebcal = this.getHebCalByDate(new Date())

        if (hebcal.yomtov) {
            return true
        }
        return false
    }
}
